import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Eye, Edit, Trash2, ArrowLeft, FileText, Upload } from 'lucide-react'
import toast from 'react-hot-toast'
import LoadingSpinner from '../components/LoadingSpinner'
import { useFirebase } from '../contexts/FirebaseContext'
import DOMPurify from 'dompurify'

const Subnotes = () => {
  const navigate = useNavigate()
  const { id: alertId } = useParams()
  const { getAlertSubnotes, updateAlertSubnote, deleteAlertSubnote, getAlerts, addAlertSubnote, uploadFile } = useFirebase()

  const [loading, setLoading] = useState(true)
  const [alertMeta, setAlertMeta] = useState(null)
  const [subnotes, setSubnotes] = useState([])
  const [viewItem, setViewItem] = useState(null)
  const [editItem, setEditItem] = useState(null)
  const [editDesc, setEditDesc] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [subOpen, setSubOpen] = useState(false)
  const [subDesc, setSubDesc] = useState('')
  const [saving, setSaving] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [viewMode, setViewMode] = useState('javascript') // 'javascript', 'preview', 'html', or 'original'
  const [replaceFile, setReplaceFile] = useState(null)
  const [replacingFile, setReplacingFile] = useState(false)
  const fileInputRef = useRef(null)
  const replaceFileInputRef = useRef(null)

  useEffect(() => {
    load()
  }, [alertId])

  const load = async () => {
    try {
      setLoading(true)
      const [allAlerts, subs] = await Promise.all([
        getAlerts().catch(() => []),
        getAlertSubnotes(alertId).catch(() => [])
      ])
      const found = (allAlerts || []).find(a => a.id === alertId)
      setAlertMeta(found || null)
      setSubnotes(subs || [])
    } catch (e) {
      console.error('Failed to load subnotes:', e)
      toast.error('Failed to load subnotes')
    } finally {
      setLoading(false)
    }
  }

  const onUploadWordClick = () => {
    if (subnotes.length >= 1) {
      toast.error('Limit reached: Only one subnote is allowed per note. Please delete the existing subnote first.', {
        duration: 4000,
        icon: '⚠️'
      })
      setMenuOpen(false)
      return
    }
    setMenuOpen(false)
    if (fileInputRef.current) fileInputRef.current.click()
  }

  const convertWordToHTML = async (file) => {
    const { renderAsync } = await import('docx-preview')
    const arrayBuffer = await file.arrayBuffer()
    
    // Extract footer content from Word XML using JSZip
    let extractedFooters = []
    try {
      const JSZip = (await import('jszip')).default
      const zip = await JSZip.loadAsync(arrayBuffer)
      
      console.log('Available files in Word document:', Object.keys(zip.files))
      
      // Check for footer files in the document
      const footerFiles = Object.keys(zip.files).filter(name => 
        name.toLowerCase().includes('footer') && name.endsWith('.xml')
      )
      
      console.log('Found footer files:', footerFiles)
      
      for (const footerFile of footerFiles) {
        const content = await zip.files[footerFile].async('text')
        console.log('Footer XML content:', content)
        
        // Extract all text from XML - including text within tags
        const textMatches = content.match(/<w:t[^>]*>([^<]*)<\/w:t>/g)
        const instrTextMatches = content.match(/<w:instrText[^>]*>([^<]*)<\/w:instrText>/g)
        const fldCharMatches = content.match(/<w:fldChar[^>]*>/g)
        
        let footerParts = []
        let hasPageField = false
        
        // Check if footer contains PAGE field
        if (instrTextMatches) {
          instrTextMatches.forEach(match => {
            const text = match.replace(/<w:instrText[^>]*>|<\/w:instrText>/g, '').trim()
            if (text.includes('PAGE')) {
              hasPageField = true
            }
          })
        }
        
        // Extract regular text (this includes the actual page number)
        if (textMatches) {
          textMatches.forEach(match => {
            const text = match.replace(/<w:t[^>]*>|<\/w:t>/g, '')
            if (text) {
              footerParts.push(text)
            }
          })
        }
        
        if (footerParts.length > 0) {
          const footerText = footerParts.join(' ').trim()
          console.log('Extracted footer text:', footerText)
          if (footerText) {
            extractedFooters.push(footerText)
          }
        }
      }
    } catch (e) {
      console.error('Could not extract footer from Word XML:', e)
    }
    
    const tempContainer = document.createElement('div')
    tempContainer.className = 'docx'
    await renderAsync(arrayBuffer, tempContainer, null, {
      className: 'docx',
      inWrapper: true,
      ignoreWidth: false,
      ignoreHeight: false,
      ignoreFonts: false,
      breakPages: true,
      ignoreLastRenderedPageBreak: true,
      useBase64URL: true,
      experimental: true,
      renderFooters: true,
      renderHeaders: true,
    })
    
    // Log rendered HTML for debugging
    console.log('=== DOCX PREVIEW DEBUG ===')
    console.log('Container HTML length:', tempContainer.innerHTML.length)
    console.log('First 1000 chars:', tempContainer.innerHTML.substring(0, 1000))
    console.log('Last 1000 chars:', tempContainer.innerHTML.substring(tempContainer.innerHTML.length - 1000))
    
    // Extract footer content including page numbers - comprehensive search
    const footerSelectors = [
      '.docx-footer',
      'footer',
      '[class*="footer"]',
      '[class*="Footer"]',
      '[class*="ftr"]',
      'section[class*="footer"]',
      'div[class*="footer"]',
      'p[class*="footer"]',
      '.docx-wrapper section:last-child',
      'section:last-of-type',
      '[role="contentinfo"]',
      'div[style*="position: absolute"]',
      'div[style*="bottom"]'
    ]
    
    const footerContents = []
    const processedElements = new Set()
    
    // Try each selector
    footerSelectors.forEach(selector => {
      try {
        const elements = tempContainer.querySelectorAll(selector)
        if (elements.length > 0) {
          console.log(`Selector "${selector}" found ${elements.length} elements`)
        }
        elements.forEach(element => {
          if (!processedElements.has(element)) {
            const text = element.textContent.trim()
            if (text && text.length > 0) {
              console.log(`Footer content from "${selector}":`, text)
              footerContents.push(text)
              processedElements.add(element)
            }
          }
        })
      } catch (e) {
        // Ignore invalid selectors
      }
    })
    
    // Deep scan: Check ALL elements for potential page numbers
    const allElements = tempContainer.querySelectorAll('*')
    console.log(`Total elements to scan: ${allElements.length}`)
    
    let isolatedNumbersFound = 0
    Array.from(allElements).forEach(element => {
      const text = element.textContent.trim()
      
      // Get only direct text nodes of this element (not from children)
      const directText = Array.from(element.childNodes)
        .filter(node => node.nodeType === Node.TEXT_NODE)
        .map(node => node.textContent.trim())
        .join(' ')
      
      // Look for isolated numbers (likely page numbers)
      if (directText && /^\d+$/.test(directText) && directText.length <= 4) {
        const isLeaf = element.children.length === 0
        const hasOnlySimpleChild = element.children.length === 1 && 
          ['SPAN', 'A', 'STRONG', 'EM'].includes(element.children[0].tagName)
        
        if ((isLeaf || hasOnlySimpleChild) && !processedElements.has(element)) {
          isolatedNumbersFound++
          console.log(`✓ Found isolated number #${isolatedNumbersFound}:`, {
            text: directText,
            tag: element.tagName,
            class: element.className,
            style: element.getAttribute('style'),
            parent: element.parentElement?.tagName
          })
          footerContents.push(`Page ${directText}`)
          processedElements.add(element)
        }
      }
    })
    
    console.log(`Total isolated numbers found: ${isolatedNumbersFound}`)
    console.log('All footer contents:', footerContents)
    
    // Combine extracted footers with rendered footers
    const allFooterContents = [...extractedFooters, ...footerContents]
    console.log('=== FINAL FOOTER SUMMARY ===')
    console.log('Extracted from XML:', extractedFooters)
    console.log('Found in rendered HTML:', footerContents)
    console.log('Combined total:', allFooterContents)
    
    // Create footer section at the end if footer content exists
    if (allFooterContents.length > 0) {
      // Remove duplicates
      const uniqueFooters = [...new Set(allFooterContents)]
      console.log('✅ Adding footer section with content:', uniqueFooters)
      
      const footerSection = document.createElement('div')
      footerSection.style.cssText = 'position: relative; width: 100%;'
      
      const footerBar = document.createElement('div')
      footerBar.style.cssText = 'margin-top: 32px; padding-top: 12px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 12px; display: flex; align-items: center; justify-content: center; gap: 10px;'
      
      const footerText = uniqueFooters.join('  •  ')
      const span = document.createElement('span')
      span.textContent = footerText
      footerBar.appendChild(span)
      
      footerSection.appendChild(footerBar)
      tempContainer.appendChild(footerSection)
    } else {
      console.warn('⚠️ No footer content found to display')
    }
    
    // Preserve styles generated by docx-preview (required for footer positioning)
    const styleTags = Array.from(tempContainer.querySelectorAll('style'))
      .map(s => s.outerHTML)
      .join('\n')
    const html = `${styleTags}<div class="docx">${tempContainer.innerHTML}</div>`
    const safeHtml = DOMPurify.sanitize(html, { ADD_ATTR: ['style'], ADD_TAGS: ['style'] })
    return safeHtml
  }

  const onWordSelected = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    const fileSizeMB = file.size / (1024 * 1024)
    
    try {
      setSaving(true)
      
      // Upload Word document as-is to Firebase Storage (no HTML conversion)
      toast.loading(`Uploading Word document (${fileSizeMB.toFixed(1)}MB)...`, { id: 'uploading' })
      const url = await uploadFile(file, `alerts/${alertId}/subnotes`)
      toast.dismiss('uploading')
      
      // Store only metadata in Firestore (no HTML content)
      await addAlertSubnote(alertId, {
        title: file.name,
        message: '', // No HTML content - just file metadata
        fileUrl: url,
        fileName: file.name,
        fileType: file.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        isFileContent: true,
        contentType: 'file',
        isWordFile: true
      })
      
      toast.success('Word document uploaded successfully!')
      await load()
    } catch (err) {
      toast.dismiss('converting')
      console.error('Failed to upload file subnote:', err)
      
      // Provide more helpful error messages
      if (err.message?.includes('too large')) {
        // Extract the clean error message (remove "Failed to create subnote:" prefix if present)
        const cleanMessage = err.message.replace('Failed to create subnote: ', '')
        toast.error(cleanMessage, { duration: 6000 })
      } else if (err.message?.includes('longer than')) {
        toast.error('File too large: The document content exceeds storage limits. Try a smaller file or contact support.', { duration: 5000 })
      } else {
        toast.error('Failed to upload file: ' + err.message, { duration: 5000 })
      }
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ''
      setSaving(false)
    }
  }

  const handleAddSubnote = () => {
    if (subnotes.length >= 1) {
      toast.error('Limit reached: Only one subnote is allowed per note. Please delete the existing subnote first.', {
        duration: 4000,
        icon: '⚠️'
      })
      return
    }
    setSubOpen(true)
    setSubDesc('')
  }

  const saveSubnote = async () => {
    if (!subDesc.trim()) {
      toast.error('Please enter a description')
      return
    }
    try {
      setSaving(true)
      await addAlertSubnote(alertId, { title: '', message: subDesc.trim() })
      toast.success('Subnote added')
      setSubOpen(false)
      setSubDesc('')
      await load()
    } catch (e) {
      console.error('Failed to add subnote:', e)
      toast.error('Failed to add subnote')
    } finally {
      setSaving(false)
    }
  }

  const openEdit = (item) => {
    // Open edit modal with Word file preview
    setEditItem(item)
    setEditDesc(item.message || '')
    setViewMode('original') // Show original view by default
  }

  const saveEdit = async () => {
    if (!editItem) return
    if (!editDesc.trim()) {
      toast.error('Please enter a description')
      return
    }
    try {
      await updateAlertSubnote(alertId, editItem.id, { message: editDesc.trim() })
      toast.success('Subnote updated')
      setEditItem(null)
      setEditDesc('')
      await load()
    } catch (e) {
      console.error('Failed to update subnote:', e)
      toast.error('Failed to update subnote')
    }
  }

  const onReplaceFileSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !editItem) return
    
    try {
      setReplacingFile(true)
      toast.loading('Replacing Word document...', { id: 'replacing' })
      
      // Convert new Word file using convertWordToHTML
      const htmlContent = await convertWordToHTML(file)
      const url = await uploadFile(file, `alerts/${alertId}/subnotes`)
      
      // Update the subnote with new content
      await updateAlertSubnote(alertId, editItem.id, {
        message: htmlContent,
        fileUrl: url,
        fileName: file.name,
        fileType: file.type || 'application/octet-stream',
        isFileContent: true,
        contentType: 'html-inline'
      })
      
      toast.dismiss('replacing')
      toast.success('Word file replaced successfully!')
      
      setEditItem(null)
      setEditDesc('')
      setReplaceFile(null)
      await load()
    } catch (error) {
      toast.dismiss('replacing')
      toast.error('Failed to replace file: ' + error.message)
      console.error('Replace error:', error)
    } finally {
      setReplacingFile(false)
      if (replaceFileInputRef.current) {
        replaceFileInputRef.current.value = ''
      }
    }
  }

  const doDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteAlertSubnote(alertId, confirmDelete.id)
      toast.success('Subnote deleted')
      setConfirmDelete(null)
      await load()
    } catch (e) {
      console.error('Failed to delete subnote:', e)
      toast.error('Failed to delete subnote')
    }
  }

  // Removed convertWordToHtmlCss function (converter feature removed)

  const removedConverterFunction = () => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result
          
          // Extract footer content from Word XML using JSZip (same as convertWordToHTML)
          let extractedFooters = []
          try {
            const JSZip = (await import('jszip')).default
            const zip = await JSZip.loadAsync(arrayBuffer)
            
            const footerFiles = Object.keys(zip.files).filter(name => 
              name.toLowerCase().includes('footer') && name.endsWith('.xml')
            )
            
            console.log('[mammoth] Found footer files:', footerFiles)
            
            for (const footerFile of footerFiles) {
              const content = await zip.files[footerFile].async('text')
              console.log('[mammoth] Footer XML:', content.substring(0, 500))
              
              const textMatches = content.match(/<w:t[^>]*>([^<]*)<\/w:t>/g)
              const instrTextMatches = content.match(/<w:instrText[^>]*>([^<]*)<\/w:instrText>/g)
              
              let footerParts = []
              let hasPageField = false
              
              if (instrTextMatches) {
                instrTextMatches.forEach(match => {
                  const text = match.replace(/<w:instrText[^>]*>|<\/w:instrText>/g, '').trim()
                  if (text.includes('PAGE')) {
                    hasPageField = true
                  }
                })
              }
              
              if (textMatches) {
                textMatches.forEach(match => {
                  const text = match.replace(/<w:t[^>]*>|<\/w:t>/g, '')
                  if (text) {
                    footerParts.push(text)
                  }
                })
              }
              
              if (footerParts.length > 0) {
                const footerText = footerParts.join(' ').trim()
                console.log('[mammoth] Extracted footer:', footerText)
                if (footerText) {
                  extractedFooters.push(footerText)
                }
              }
            }
          } catch (e) {
            console.error('[mammoth] Could not extract footer from Word XML:', e)
          }
          
          const mammoth = await import('mammoth/mammoth.browser.min.js')
          
          const options = {
            styleMap: [
              "p[style-name='Heading 1'] => h1:fresh",
              "p[style-name='Heading 2'] => h2:fresh", 
              "p[style-name='Heading 3'] => h3:fresh",
              "p[style-name='Heading 4'] => h4:fresh",
              "p[style-name='Title'] => h1:fresh",
              "p[style-name='Subtitle'] => h2:fresh",
              "p[style-name='Footer'] => p.footer:fresh",
            ],
            includeDefaultStyleMap: true,
            includeEmbeddedStyleMap: true,
            preserveStyleMap: true,
            convertImage: mammoth.images.imgElement(function(image) {
              return image.read("base64").then(function(imageBuffer) {
                return {
                  src: "data:" + image.contentType + ";base64," + imageBuffer
                }
              })
            }),
            includeFooters: true
          }
          
          const result = await mammoth.convertToHtml({ arrayBuffer }, options)
          let html = result.value
          
          // Parse HTML to enhance styles with inline CSS
          const parser = new DOMParser()
          const doc = parser.parseFromString(html, 'text/html')
          
          // Process all elements to ensure inline styles are preserved and enhanced
          const processElement = (element) => {
            if (element.nodeType !== 1) return
            
            // Get existing style
            let styleObj = {}
            const existingStyle = element.getAttribute('style')
            if (existingStyle) {
              existingStyle.split(';').forEach(rule => {
                const [prop, value] = rule.split(':').map(s => s.trim())
                if (prop && value) {
                  styleObj[prop] = value
                }
              })
            }
            
            // Add default styles based on element type
            switch(element.tagName.toLowerCase()) {
              case 'h1':
                if (!styleObj['font-size']) styleObj['font-size'] = '2.5em'
                if (!styleObj['font-weight']) styleObj['font-weight'] = 'bold'
                if (!styleObj['color']) styleObj['color'] = '#2c3e50'
                if (!styleObj['margin']) styleObj['margin'] = '1.5em 0 0.5em 0'
                break
              case 'h2':
                if (!styleObj['font-size']) styleObj['font-size'] = '2em'
                if (!styleObj['font-weight']) styleObj['font-weight'] = 'bold'
                if (!styleObj['color']) styleObj['color'] = '#34495e'
                if (!styleObj['margin']) styleObj['margin'] = '1.5em 0 0.5em 0'
                break
              case 'h3':
                if (!styleObj['font-size']) styleObj['font-size'] = '1.5em'
                if (!styleObj['font-weight']) styleObj['font-weight'] = 'bold'
                if (!styleObj['color']) styleObj['color'] = '#34495e'
                if (!styleObj['margin']) styleObj['margin'] = '1.5em 0 0.5em 0'
                break
              case 'p':
                if (!styleObj['margin']) styleObj['margin'] = '0.8em 0'
                if (!styleObj['line-height']) styleObj['line-height'] = '1.6'
                break
              case 'ul':
              case 'ol':
                if (!styleObj['margin']) styleObj['margin'] = '1em 0'
                if (!styleObj['padding-left']) styleObj['padding-left'] = '2em'
                break
              case 'li':
                if (!styleObj['margin']) styleObj['margin'] = '0.3em 0'
                break
              case 'img':
                if (!styleObj['max-width']) styleObj['max-width'] = '100%'
                if (!styleObj['height']) styleObj['height'] = 'auto'
                if (!styleObj['margin']) styleObj['margin'] = '10px 0'
                if (!styleObj['display']) styleObj['display'] = 'block'
                break
              case 'table':
                if (!styleObj['border-collapse']) styleObj['border-collapse'] = 'collapse'
                if (!styleObj['width']) styleObj['width'] = '100%'
                if (!styleObj['margin']) styleObj['margin'] = '1em 0'
                break
              case 'th':
              case 'td':
                if (!styleObj['border']) styleObj['border'] = '1px solid #ddd'
                if (!styleObj['padding']) styleObj['padding'] = '8px 12px'
                if (element.tagName.toLowerCase() === 'th') {
                  if (!styleObj['background-color']) styleObj['background-color'] = '#f8f9fa'
                  if (!styleObj['font-weight']) styleObj['font-weight'] = 'bold'
                }
                break
              case 'strong':
              case 'b':
                if (!styleObj['font-weight']) styleObj['font-weight'] = 'bold'
                break
              case 'em':
              case 'i':
                if (!styleObj['font-style']) styleObj['font-style'] = 'italic'
                break
              case 'u':
                if (!styleObj['text-decoration']) styleObj['text-decoration'] = 'underline'
                break
              case 'a':
                if (!styleObj['color']) styleObj['color'] = '#1e40af'
                if (!styleObj['text-decoration']) styleObj['text-decoration'] = 'underline'
                break
            }
            
            // Build inline style string
            const styleString = Object.entries(styleObj)
              .map(([prop, value]) => `${prop}: ${value}`)
              .join('; ')
            
            if (styleString) {
              element.setAttribute('style', styleString + ';')
            }
            
            // Process children
            Array.from(element.children).forEach(processElement)
          }
          
          // Process all body children
          Array.from(doc.body.children).forEach(processElement)
          
          // Extract and convert footer content to code blocks at the end
          const footers = doc.body.querySelectorAll('footer, [class*="footer"], [id*="footer"], p.footer, .footer')
          const footerContents = []
          
          footers.forEach(footer => {
            const footerText = footer.textContent.trim()
            if (footerText) {
              footerContents.push(footerText)
              // Remove original footer element
              footer.remove()
            }
          })
          
          // Combine extracted footers from XML with rendered footers
          const allFooterContents = [...extractedFooters, ...footerContents]
          console.log('[mammoth] All footer contents:', allFooterContents)
          
          // Append footer section at the end if footer content exists
          if (allFooterContents.length > 0) {
            // Remove duplicates
            const uniqueFooters = [...new Set(allFooterContents)]
            
            const footerSection = doc.createElement('div')
            footerSection.setAttribute('style', 'position: relative; width: 100%;')
            
            const footerBar = doc.createElement('div')
            footerBar.setAttribute('style', 'margin-top: 32px; padding-top: 12px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 12px; display: flex; align-items: center; justify-content: center; gap: 10px;')
            
            const footerText = uniqueFooters.join('  •  ')
            const span = doc.createElement('span')
            span.textContent = footerText
            footerBar.appendChild(span)
            
            footerSection.appendChild(footerBar)
            doc.body.appendChild(footerSection)
          }
          
          // Get final HTML with inline styles
          let finalHtml = doc.body.innerHTML
            .replace(/<p><\/p>/g, '') // Remove empty paragraphs
            .trim()
          
          // Wrap in container with inline styles
          const containerStyle = "font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #333; max-width: 100%; margin: 0 auto; padding: 20px; background: #fff;"
          finalHtml = `<div style="${containerStyle}">\n${finalHtml}\n</div>`
          
          // For CSS view, just provide a note that inline styles are used
          const css = `/* Auto-generated from Word Document: ${file.name} */
/* Generated on: ${new Date().toLocaleString()} */
/* Note: All styles are inline in the HTML for easy copy-paste */

/* This document uses inline CSS for maximum compatibility */
/* All formatting from the Word document has been preserved inline */
`
          
          resolve({
            html: finalHtml,
            css: css,
            originalFileName: file.name,
            metadata: {
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type,
              convertedAt: new Date().toISOString(),
              inlineStyles: true
            }
          })
          
        } catch (error) {
          console.error('Error converting Word to HTML/CSS:', error)
          reject(error)
        }
      }
      reader.onerror = reject
      reader.readAsArrayBuffer(file)
    })
  }

  // Converter/editor functions removed

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button onClick={() => navigate('/alerts')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">Subnotes</h1>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                subnotes.length >= 1 
                  ? 'bg-red-100 text-red-800' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {subnotes.length}/1 {subnotes.length >= 1 ? '(Limit Reached)' : ''}
              </span>
            </div>
            {alertMeta && (
              <div className="text-sm text-gray-600 truncate max-w-xl">For: {alertMeta.title || alertId}</div>
            )}
          </div>
        </div>
        <div className="relative">
          <button 
            onClick={() => setMenuOpen((v) => !v)} 
            disabled={subnotes.length >= 1}
            className={`btn-primary ${subnotes.length >= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={subnotes.length >= 1 ? 'Maximum one subnote per note allowed' : 'Add a subnote'}
          >
            Add Subnote
          </button>
          {subnotes.length >= 1 && (
            <div className="absolute right-0 mt-2 w-64 bg-yellow-50 border border-yellow-200 rounded-lg shadow-lg p-3 z-10">
              <div className="flex items-start gap-2">
                <svg className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="text-sm">
                  <p className="text-yellow-800 font-medium">Limit Reached</p>
                  <p className="text-yellow-700">Only one subnote is allowed per note. Delete the existing subnote to add a new one.</p>
                </div>
              </div>
            </div>
          )}
          {menuOpen && subnotes.length < 1 && (
            <div className="absolute right-0 mt-2 w-56 bg-white border rounded-lg shadow-lg z-10">
              
              <button className="w-full text-left px-4 py-2 hover:bg-gray-50" onClick={onUploadWordClick}>Upload Word File</button>
            </div>
          )}
          <input ref={fileInputRef} type="file" className="hidden" accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={onWordSelected} />
        </div>
      </div>

      {/* Info Banner */}
      {subnotes.length === 0 && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <span className="font-medium">Note:</span> You can add only <span className="font-semibold">one subnote per note</span>. Upload a Word document or add text content to get started.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* List */}

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Subnotes ({subnotes.length}/1)</h3>
        </div>
        <div className="card-body p-0">
          {subnotes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subnotes.map(sn => {
                    const hasHtmlTags = sn.message && (sn.message.includes('<') || sn.isFileContent)
                    const isWordDoc = sn.isWordFile || (sn.fileName && (sn.fileName.endsWith('.docx') || sn.fileName.endsWith('.doc')))
                    const isHtmlContent = sn.contentType === 'html'
                    const showHtmlPreview = isHtmlContent || (hasHtmlTags && !isWordDoc)
                    const isChunked = sn.isChunked
                    const wasCompressed = sn.wasCompressed
                    
                    return (
                    <tr key={sn.id} className="hover:bg-gray-50">
                      <td className="max-w-xl">
                        {showHtmlPreview ? (
                          <div className="space-y-2">
                            <div 
                              className="text-gray-900 prose prose-sm max-w-none"
                              style={{ 
                                maxHeight: '100px', 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                              dangerouslySetInnerHTML={{ __html: sn.message || '' }} 
                            />
                            {(isChunked || wasCompressed) && (
                              <div className="flex gap-2">
                                {isChunked && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    📦 Large Content
                                  </span>
                                )}
                                {wasCompressed && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    🗜️ Compressed
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        ) : isWordDoc ? (
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">📄</span>
                            <div>
                              <div className="font-medium text-blue-600">{sn.fileName || 'Word Document'}</div>
                              <div className="text-xs text-gray-500">Click the eye icon to view with full formatting</div>
                              {(isChunked || wasCompressed) && (
                                <div className="flex gap-2 mt-1">
                                  {isChunked && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      📦 Large File
                                    </span>
                                  )}
                                  {wasCompressed && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      🗜️ Optimized
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="text-gray-900 whitespace-pre-wrap truncate">{sn.message || ''}</div>
                            {(isChunked || wasCompressed) && (
                              <div className="flex gap-2 mt-1">
                                {isChunked && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    📦 Large Content
                                  </span>
                                )}
                                {wasCompressed && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    🗜️ Compressed
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="text-sm text-gray-600">
                        {sn.createdAt ? (sn.createdAt.toDate ? new Date(sn.createdAt.toDate()).toLocaleDateString('en-GB') : new Date(sn.createdAt).toLocaleDateString('en-GB')) : 'N/A'}
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <button className="text-blue-600 hover:text-blue-900" title="View" onClick={() => setViewItem(sn)}>
                            <Eye className="h-4 w-4"/>
                          </button>
                          <button className="text-green-600 hover:text-green-900" title="Edit" onClick={() => openEdit(sn)}>
                            <Edit className="h-4 w-4"/>
                          </button>
                          <button className="text-red-600 hover:text-red-900" title="Delete" onClick={() => setConfirmDelete(sn)}>
                            <Trash2 className="h-4 w-4"/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">No subnotes yet</p>
            </div>
          )}
        </div>
      </div>

      {/* View Modal */}
      {viewItem && (() => {
        const hasFileUrl = viewItem.fileUrl
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setViewItem(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-[98vw] h-[95vh] overflow-hidden flex flex-col">
            {hasFileUrl ? (
              <div className="w-full h-full flex-1">
                <iframe
                  src={`https://docs.google.com/gview?url=${encodeURIComponent(viewItem.fileUrl)}&embedded=true`}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title="Document Viewer"
                  style={{ display: 'block', border: 'none' }}
                >
                  <p>Your browser does not support iframes. <a href={viewItem.fileUrl} target="_blank" rel="noopener noreferrer">Download the file</a> to view it.</p>
                </iframe>
              </div>
            ) : (
              <div className="w-full flex-1 overflow-auto p-4">
                <div className="text-gray-600">
                  {viewItem.message || 'No content available'}
                </div>
              </div>
            )}
            <div className="flex justify-end p-2 border-t shrink-0">
              <button onClick={() => { setViewItem(null); setViewMode('javascript'); }} className="btn-outline">Close</button>
            </div>
          </div>
        </div>
        )
      })()}

      {/* Edit Modal */}
      {editItem && (() => {
        const hasFileUrl = editItem.fileUrl
        const isWordFile = editItem.isWordFile || (hasFileUrl && (editItem.fileName?.endsWith('.docx') || editItem.fileName?.endsWith('.doc')))
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={() => { setEditItem(null); setEditDesc(''); }} />
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-[98vw] h-[95vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-4 border-b shrink-0">
                <h2 className="text-xl font-bold text-gray-900">Edit Subnote</h2>
                <button
                  onClick={() => { setEditItem(null); setEditDesc(''); }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* File Info and Replace Button */}
              {isWordFile && hasFileUrl && (
                <div className="p-4 bg-blue-50 border-b border-blue-200 shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-semibold text-gray-900">{editItem.fileName || 'Word Document'}</p>
                        <p className="text-sm text-gray-600">Original uploaded file</p>
                      </div>
                    </div>
                    <button
                      onClick={() => replaceFileInputRef.current?.click()}
                      disabled={replacingFile}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Upload className="h-4 w-4" />
                      {replacingFile ? 'Replacing...' : 'Replace Word File'}
                    </button>
                  </div>
                </div>
              )}

              {/* Content Preview */}
              <div className="flex-1 overflow-hidden">
                {isWordFile && hasFileUrl ? (
                  <iframe
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(editItem.fileUrl)}&embedded=true`}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    title="Word Document Preview"
                    style={{ display: 'block', border: 'none' }}
                  >
                    <p>Your browser does not support iframes. <a href={editItem.fileUrl} target="_blank" rel="noopener noreferrer">Download the file</a> to view it.</p>
                  </iframe>
                ) : (
                  <div className="h-full overflow-auto p-6">
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(editItem.message || 'No content available', {
                          ADD_ATTR: ['style'],
                          ALLOWED_ATTR: ['style', 'src', 'alt', 'href', 'class', 'id']
                        })
                      }} 
                    />
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-2 p-4 border-t shrink-0">
                <button 
                  onClick={() => { setEditItem(null); setEditDesc(''); }} 
                  className="btn-outline"
                  disabled={replacingFile}
                >
                  Cancel
                </button>
              </div>
            </div>
            <input 
              ref={replaceFileInputRef} 
              type="file" 
              className="hidden" 
              accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" 
              onChange={onReplaceFileSelect} 
            />
          </div>
        )
      })()}

      {/* Delete Confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmDelete(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="text-lg font-semibold text-gray-900 mb-2">Delete subnote?</div>
            <div className="text-sm text-gray-600 mb-6">This action cannot be undone.</div>
            <div className="flex justify-end space-x-2">
              <button onClick={() => setConfirmDelete(null)} className="btn-outline">Cancel</button>
              <button onClick={doDelete} className="btn-danger">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Subnote Modal */}
      {subOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSubOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="text-lg font-semibold text-gray-900 mb-4">Add Subnote</div>
            <textarea
              className="input w-full min-h-[140px]"
              placeholder="Enter description"
              value={subDesc}
              onChange={(e) => setSubDesc(e.target.value)}
              disabled={saving}
            />
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setSubOpen(false)} className="btn-outline" disabled={saving}>Cancel</button>
              <button onClick={saveSubnote} className="btn-primary disabled:opacity-50" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Converter/Editor feature removed */}
    </div>
  )
}

export default Subnotes
